import store from '../store/index'
import TimeManager from "./TimeManager";

function formatNumber(decimalCount, amount, symbol, thousands = ",", decimal = ".") {
    try {
        decimalCount = Math.abs(decimalCount);
        decimalCount = isNaN(decimalCount) ? 2 : decimalCount;

        const negativeSign = amount < 0 ? "-" : "";

        // Convert the amount to a fixed-point notation
        amount = Number(amount).toFixed(decimalCount);

        // Use regex to remove unnecessary trailing zeros and the decimal point if it is an integer
        let [integerPart, fractionalPart] = amount.split('.');

        // Trimming the fractional part of trailing zeros if necessary
        if (fractionalPart) {
            fractionalPart = fractionalPart.replace(/0+$/, '');
        }

        let i = Math.abs(parseInt(integerPart)).toString(); // Ensure the integer part is positive for the split
        let j = i.length > 3 ? i.length % 3 : 0;

        return (
            symbol +
            ' ' +
            negativeSign +
            (j ? i.substr(0, j) + thousands : '') +
            i.substr(j).replace(/(\d{3})(?=\d)/g, '$1' + thousands) +
            (fractionalPart ? decimal + fractionalPart : '')
        );
    } catch (e) {
        console.error(e);
    }
}

export function sanitizeDate(dateString) {
    const dateRegex = /^(\d{4}-\d{2}-\d{2})(.*)/;
    const match = dateString.match(dateRegex);

    if (match) {
        return match[1];
    }
    return dateString;
}

export function getResponseDetails(code) {
    const responseCodes = {
        "884": {
            code:'884',
            entity: "Client",
            message: "Invalid customer TPIN was provided",
            httpStatus: 400,
            action: "edit"
        },
        "894": {
            code:'894',
            entity: "Client",
            message: "An error regarding server communication occurred.",
            httpStatus: 500,
            action: "retry"
        },
        "895": {
            code: '895',
            entity: "Client",
            message: "An error regarding unallowed Request Method occurred.",
            httpStatus: 405,
            action: "none"
        },
        "896": {
            code: '896',
            entity: "Client",
            message: "An error regarding Request Status occurred.",
            httpStatus: 400,
            action: "retry"
        },
        "899": {
            code: '899',
            entity: "Client",
            message: "An error regarding Client occurred.",
            httpStatus: 400,
            action: "reregister_and_retry"
        },
        "910": {
            code: '910',
            entity: "Server",
            message: "Request parameter error",
            httpStatus: 400,
            action: "edit"
        },
        "921": {
            code: '921',
            entity: "Server",
            message: "Sales or sales invoice data which is declared cannot be received.",
            httpStatus: 400,
            action: "edit"
        },
        "924": {
            code: '924',
            entity: "Client",
            message: "Sales or sales invoice data which is declared is a duplicate.",
            httpStatus: 409,
            action: "retry"
        },
        "930": {
            code: '930',
            entity: "Client",
            message: "The specified invoice could not be found. Please verify [orgInvcNo] and try again [orgInvcNo].",
            httpStatus: 400,
            action: "edit"
        },
        "931": {
            code: '931',
            entity: "Server",
            message: "The credit note amount exceeds the original invoice amount.",
            httpStatus: 406,
            action: "edit"
        },
        "932": {
            code: '932',
            entity: "Server",
            message: "The item specified in the credit note does not exist on the original invoice. [itemCd].",
            httpStatus: 400,
            action: "edit"
        },
        "934": {
            code: '934',
            entity: "Server",
            message: "The quantity specified in the credit note exceeds the quantity in the original invoice.",
            httpStatus: 400,
            action: "edit"
        },
        "935": {
            code: '935',
            entity: "Server",
            message: "The credit note contains information that does not match the original invoice data.",
            httpStatus: 406,
            action: "edit"
        },
        "992": {
            code: '992',
            entity: "Server",
            message: "There is an error during modification",
            httpStatus: 500,
            action: "edit"
        },
        "999": {
            code: '999',
            entity: "Server",
            message: "There is an unknown error. Please ask an administrator",
            httpStatus: 500,
            action: "retry"
        },
        // success
        '000': {
            code: '000',
            entity: "Server",
            message: "Invoice was successfully sent",
            httpStatus: 200,
            action: "none"
        }
    };

    const response = responseCodes[code];

    if (!response) {
        return {
            status: "unknown",
            entity: "Unknown",
            message: "Unknown response code",
            httpStatus: 500,
            action: "none"
        };
    }

    return {
        status: response.httpStatus >= 400 ? "error" : "success",
        entity: response.entity,
        message: response.message,
        httpStatus: response.httpStatus,
        action: response.action
    };
}


const fileTypes = [
    { mime: 'text/csv', extension: 'csv' },
    { mime: 'application/pdf', extension: 'pdf' },
    { mime: 'application/vnd.ms-excel', extension: 'xls' },
    {
        mime: 'application/vnd.ms-excel',
        extension: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }
];

export const getFileType = (response) => {
    return fileTypes.find((ty) => ty.mime === response.headers['content-type']) ||
        { mime: 'application/octet-stream', extension: 'bin' };
};

export function base64ToBlob(base64Data, contentType = '', sliceSize = 512) {
    const byteCharacters = atob(base64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);
        const byteNumbers = new Array(slice.length);

        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
}

export const openFile = (response, fileName) => {
    try {
        let blobData;

        // Handle different response types
        if (response.data instanceof Blob) {
            blobData = response.data;
        } else if (response.data instanceof ArrayBuffer) {
            blobData = new Blob([response.data], {
                type: response.headers['content-type'] || 'application/pdf'
            });
        } else {
            throw new Error('Unsupported response data type');
        }

        // For PDFs, try opening in new tab
        if (blobData.type === 'application/pdf') {
            const pdfUrl = URL.createObjectURL(blobData);
            const pdfWindow = window.open(pdfUrl, '_blank');

            // Fallback to download if popup blocked
            if (!pdfWindow) {
                downloadFile(blobData, fileName, 'pdf');
            }
            return;
        }

        // Default download behavior
        downloadFile(blobData, fileName, getFileExtension(blobData.type));
    } catch (error) {
        console.error('Error handling file:', error);
        throw error;
    }
};

function downloadFile(blob, fileName, extension) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.${extension}`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, 100);
}

function getFileExtension(mimeType) {
    const extensions = {
        'application/pdf': 'pdf',
        'application/vnd.ms-excel': 'xls',
        'text/csv': 'csv'
    };
    return extensions[mimeType] || 'bin';
}
export const utils = {
    toPascalCase(string) {
        return string
            .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
            .replace(/^\w/, s => s.toUpperCase());
    },
    cutLength(string, limit = 45) {
        if (string.length < limit) {
            return string
        } else {
            return string.toString().substring(0, limit).concat('...')
        }
    },

    formatMoney(amount, decimalCount = 2, decimal = ".", thousands = ",", isCurrency = true) {
        let currency = store.getters.getBaseCurrency.Symbol;
        if (isNaN(amount)) return `K 0`;
        amount = this.roundAmount(amount,decimalCount);
        if (!isCurrency) {
            return formatNumber(decimalCount, amount, '', thousands, decimal);
        }
        return formatNumber(decimalCount, amount, currency, thousands, decimal);
    },

    formatCurrency(amount, decimalCount = 2,currency) {
        if (!currency) currency = ``;
        return formatNumber(decimalCount, amount, currency);
    },

    formatQTY(amount) {
        amount = this.roundAmount(amount,3);
        return this.formatMoney(amount, 3, '.', ',',false);
    },

    roundAmount(amount , decimalPlaces = 2){
        if (typeof amount === 'string'){
            amount = amount.replace(`,`,``);
            amount = Number(amount);
        }
        amount = amount?.toFixed(decimalPlaces);
        return Number(amount);
    },

    removeIf(arr, predicate) {
        return arr.filter(predicate);
    },
    remove(arr, obj) {
        return arr.filter(v => v !== obj)
    },

    displayDate(date , isDateTime = true) {
        return TimeManager.shortenDisplay(date , isDateTime);
    },

    displayJavaDate(date) {
        return TimeManager.shortJavaDateTimeObject(date);
    },

    getNow() {
        let date = new Date();
        return date.toISOString().split('T')[0];
    },

    showLoading: () => store.commit(`setLoadingState`, true),
    hideLoading: () => store.commit(`setLoadingState`, false),
}

